import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnVisibility, QBodyLayout, QBox, QTabHeader, QTabs } from '@qualio/ui-components';
import EventTable from '../../../components/EventTable/EventTable';
import EventListHeader from './EventListHeader/EventListHeader';
import { useSearchParams } from 'react-router-dom';
import { TaskTableV2 } from '../../../components/TaskTableV2/TaskTableV2';
import { EVENT_TABLE_COLUMN_IDS, EVENT_TABLE_INITIAL_VISIBILITY } from '../../../components/EventTable/columns';
import { eventsTablePersistanceKey } from '../../../types/common/constants';
import { useFlags } from '../../../external/LaunchDarklyAdapter';

const EventListView = () => {
  const { t } = useTranslation();
  const isConfigurableColumnsEnabled = useFlags('configurableColumnsEventList');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? 'events');
  const showTasks = activeTab === 'tasks';
  const showEvents = activeTab === 'events';
  const handleChangeTab = (header: QTabHeader) => {
    if (header.id === 'tab-events') {
      setSearchParams({ tab: 'events' });
      setActiveTab('events');
    }
    if (header.id === 'tab-tasks') {
      setSearchParams({ tab: 'tasks', status: 'open' });
      setActiveTab('tasks');
    }
  };
  return (
    <QBox aria-label="event-list-view">
      {/* Outermost provider: ExportButton lives in the header, EventTable in the tab, and both
          need the same selection. It sits outside QBodyLayout.Default because that component
          destructures its children as [header, ...content]. */}
      <ColumnVisibility.DefaultProvider
        enabled={isConfigurableColumnsEnabled}
        columnIds={EVENT_TABLE_COLUMN_IDS}
        initialVisibility={EVENT_TABLE_INITIAL_VISIBILITY}
        persistenceKey={eventsTablePersistanceKey}
      >
        <QBodyLayout.Default>
          <EventListHeader />
          <>
            <QBox mb={4}>
              <QTabs
                tabs={[
                  {
                    id: 'tab-events',
                    title: t('events.labels.events'),
                  },
                  {
                    id: 'tab-tasks',
                    title: t('common.labels.tasks'),
                  },
                ]}
                onClick={handleChangeTab}
                activeTabId={`tab-${activeTab}`}
                data-metrics="events-overview-navigation-tabs"
              />
            </QBox>
            {showEvents && <EventTable />}
            {/* Every QDataTable consumes the visibility context, and the tasks table shares
              none of these columns, so shadow it with a disabled provider. */}
            {showTasks && (
              <ColumnVisibility.DefaultProvider enabled={false}>
                <TaskTableV2 />
              </ColumnVisibility.DefaultProvider>
            )}
          </>
        </QBodyLayout.Default>
      </ColumnVisibility.DefaultProvider>
    </QBox>
  );
};

export default EventListView;
